import { House, UserPlus, UserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { KioskStatePanel } from '../../../../../components/kiosk-state-panel'
import { KioskLookupNotFound } from '../identification/KioskPatientLookupPanel'

/**
 * Scheduler "no match" outcome when kiosk registration is allowed: the shared
 * not-found panel with "Neu in der Praxis" as the way forward.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <SchedulerSearchResultsPanel onCreateRequested={() => setMode('create')} />
 * ```
 */
export function SchedulerSearchResultsPanel({
  onCreateRequested,
}: {
  onCreateRequested: () => void
}) {
  const { t } = useTranslation()
  return (
    <KioskLookupNotFound
      testId="frontdesk-scheduler-search-not-found"
      action={{
        label: t('self-service.identification.mode-create'),
        icon: UserPlus,
        onClick: onCreateRequested,
        testId: 'frontdesk-scheduler-search-create-btn',
      }}
    />
  )
}

/**
 * Scheduler "no match" outcome without kiosk registration: a reception
 * handoff with a way home (KO-72).
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <SchedulerRestrictedSearchResultsPanel onHome={exitToStart} />
 * ```
 */
export function SchedulerRestrictedSearchResultsPanel({
  onHome,
}: {
  onHome: () => void
}) {
  return <SchedulerReceptionHandoff onHome={onHome} />
}

/**
 * Reception handoff as a state of its own with a way home (KO-72). Keeps the
 * existing `frontdesk-patient-creation-reception-handoff` test id.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <SchedulerReceptionHandoff onHome={exitToStart} />
 * ```
 */
export function SchedulerReceptionHandoff({ onHome }: { readonly onHome: () => void }) {
  const { t } = useTranslation()
  return (
    <KioskStatePanel
      tone="info"
      testId="frontdesk-patient-creation-reception-handoff"
      icon={<UserRound />}
      title={t('self-service.help-reception')}
      body={t('self-service.help-reception-toast')}
      action={{
        label: t('self-service.scheduler.done.back-to-start'),
        icon: House,
        onClick: onHome,
        testId: 'frontdesk-scheduler-handoff-home-btn',
      }}
    />
  )
}
